<template>
  <v-dialog :value="open" persistent fullscreen scrollable>
    <v-card class="orden-compra-dialog-card">
      <v-toolbar flat color="primary" class="mb-2" style="flex-shrink: 0">
        <v-toolbar-title class="white--text">{{ pageTitle }}</v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn icon text class="white--text" @click="$emit('close')">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <v-card-text v-if="errorMessage" class="pa-2" style="flex-shrink: 0; flex-grow: 0">
        <v-alert type="error" dense class="mb-0">{{ errorMessage }}</v-alert>
      </v-card-text>

      <v-card-text class="orden-compra-form-area pa-2">
        <orden-compra-form
          ref="formRef"
          :initial-items="formInitialItems"
          :initial-proveedor-id="formInitialProveedorId"
          :deposito-id="depositoIdSeleccionado"
          :initial-descuento="formInitialDescuento"
          :initial-recargo="formInitialRecargo"
          @total-change="totalInfo = $event"
        />
      </v-card-text>

      <v-divider />
      <v-card-actions class="footer-actions px-4">
        <v-btn text color="grey darken-1" @click="$emit('close')">
          <v-icon left small>mdi-close</v-icon>
          Cerrar
        </v-btn>
        <v-spacer />
        <div class="footer-total-badge">
          <template v-if="totalInfo.descuento > 0 || totalInfo.recargo > 0">
            <div class="footer-breakdown">
              <span class="footer-subtotal">${{ totalInfo.subtotal }}</span>
              <div class="footer-chips">
                <v-chip v-if="totalInfo.descuento > 0" small color="green darken-1" dark>-{{ totalInfo.descuento }}%</v-chip>
                <v-chip v-if="totalInfo.recargo > 0" small color="red darken-1" dark>+{{ totalInfo.recargo }}%</v-chip>
              </div>
            </div>
            <v-icon color="grey lighten-1" class="footer-arrow">mdi-arrow-right</v-icon>
          </template>
          <div class="footer-total-main">
            <span class="footer-total-label">Total a Pagar</span>
            <span class="footer-total-amount">${{ totalInfo.total }}</span>
          </div>
        </div>
        <v-spacer />
        <v-btn color="primary" :loading="loading" depressed @click="create">
          <v-icon left small>mdi-check</v-icon>
          Confirmar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import OrdenCompraForm from "../../../../compras/pages/OrdenCompraForm.vue";
import UserConfigurationProvider from "../../../../user-configuration/providers/UserConfigurationProvider";
import OrdenCompraProvider from "../../../../compras/providers/OrdenCompraProvider";
import ProductoProvider from "../../../../producto/providers/ProductoProvider";

export default {
  name: "OrdenCompraDesdeProductosCreate",
  components: { OrdenCompraForm },
  props: {
    open: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    ordenCompraId: { type: String, default: null },
  },
  data() {
    return {
      depositoIdSeleccionado: null,
      loading: false,
      errorMessage: "",
      formInitialItems: [],
      formInitialProveedorId: null,
      formInitialDescuento: 0,
      formInitialRecargo: 0,
      totalInfo: { total: "0.00", subtotal: "0.00", descuento: 0, recargo: 0 },
    };
  },
  computed: {
    pageTitle() {
      return this.ordenCompraId ? "Modificando orden de compra" : "Creando orden de compra";
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(v) {
        if (v) {
          this.loadDeposito();
          if (this.ordenCompraId) {
            this.loadOrdenForEdit();
          } else {
            this.formInitialItems = [...(this.items || [])];
            this.formInitialProveedorId = null;
            this.formInitialDescuento = 0;
            this.formInitialRecargo = 0;
          }
        }
      },
    },
  },
  methods: {
    async loadDeposito() {
      try {
        let depositoId = UserConfigurationProvider.getDepositoIdFromCache();
        if (!depositoId) {
          const r = await UserConfigurationProvider.getUserConfiguration(true);
          const config = r.data && r.data.userConfigurationGet;
          depositoId =
            (config && config.depositoIdSeleccionado) ||
            (config && config.deposito && config.deposito.id) ||
            null;
        }
        this.depositoIdSeleccionado = depositoId;
      } catch (e) {
        console.error(e);
        this.depositoIdSeleccionado = null;
      }
    },
    async loadOrdenForEdit() {
      if (!this.ordenCompraId) return;
      this.loading = true;
      this.errorMessage = "";
      try {
        const r = await OrdenCompraProvider.findOrdenCompra(this.ordenCompraId);
        const orden = (r.data && r.data.ordenCompraFind) || null;
        if (!orden) {
          this.errorMessage = "Orden de compra no encontrada.";
          this.formInitialItems = [];
          return;
        }
        if (!orden.puedeEditarContenido) {
          this.errorMessage =
            "La orden no admite modificaciones (debe estar en borrador o ser una orden anterior sin confirmar recepción).";
          this.formInitialItems = [];
          return;
        }
        this.formInitialProveedorId =
          (orden.proveedorId && (orden.proveedorId.id || orden.proveedorId)) ||
          (orden.proveedor && orden.proveedor.id) ||
          null;
        this.formInitialDescuento = orden.descuento != null ? Number(orden.descuento) || 0 : 0;
        this.formInitialRecargo = orden.recargo != null ? Number(orden.recargo) || 0 : 0;
        const items = orden.items || [];
        const productIds = [...new Set(items.map((i) => i.producto).filter(Boolean))];
        const productResponses = await Promise.all(productIds.map((id) => ProductoProvider.findProducto(id)));
        const productMap = {};
        productResponses.forEach((res) => {
          const p = res.data && res.data.productoFind;
          if (p && (p.id || p._id)) productMap[p.id || p._id] = p;
        });
        this.formInitialItems = items.map((item) => {
          const productId = item.producto;
          let producto = productMap[productId] || null;
          const catalogo = producto && producto.precioCompra != null ? Number(producto.precioCompra) : 0;
          if (producto && item.precioUnitario != null && Number.isFinite(Number(item.precioUnitario))) {
            producto = { ...producto, precioCompra: Number(item.precioUnitario) };
          }
          return {
            producto,
            product: producto,
            cantidad: Math.max(0, Number(item.cantidad) || 0),
            precioManual: !!item.precioManual,
            precioCatalogo: catalogo,
          };
        });
      } catch (e) {
        console.error(e);
        this.errorMessage = (e && e.message) || "Error al cargar la orden de compra.";
        this.formInitialItems = [];
      } finally {
        this.loading = false;
      }
    },
    async create() {
      const form = this.$refs.formRef;
      if (!form) return;
      if (!form.valid()) {
        this.errorMessage = "Complete proveedor y al menos un producto con cantidad.";
        return;
      }
      if (!this.depositoIdSeleccionado) {
        this.errorMessage = "Seleccione un depósito en su configuración de usuario.";
        return;
      }
      this.loading = true;
      this.errorMessage = "";
      try {
        const payload = form.getPayload();
        const { proveedorId, items: itemsPayload, descuento, recargo } = payload;
        const itemsWithQty = (itemsPayload || []).filter((i) => Number(i.cantidad) >= 1);
        const input = {
          proveedorId,
          depositoId: this.depositoIdSeleccionado,
          descuento,
          recargo,
          items: itemsWithQty.map((i) => ({
            producto:
              (i.producto && (i.producto.id || i.producto._id)) ||
              (i.product && (i.product.id || i.product._id)),
            cantidad: Number(i.cantidad) || 1,
            precioUnitario: i.precioUnitario != null ? Number(i.precioUnitario) : null,
            precioManual: !!i.precioManual,
          })),
        };
        if (this.ordenCompraId) {
          const r = await OrdenCompraProvider.updateOrdenCompra(this.ordenCompraId, input);
          if (r.data && r.data.ordenCompraUpdate) {
            this.$emit("created", true);
            this.$emit("close");
            this.$router.push({ name: "OrdenCompraManagementPage" });
          }
        } else {
          const r = await OrdenCompraProvider.createOrdenCompra(input);
          if (r.data && r.data.ordenCompraCreate) {
            this.$emit("created");
            this.$emit("close");
            this.$router.push({ name: "OrdenCompraManagementPage" });
          }
        }
      } catch (e) {
        console.error(e);
        this.errorMessage = (e && e.message) || "Error al guardar la orden de compra.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.orden-compra-dialog-card {
  overflow: hidden;
}
.orden-compra-form-area {
  overflow: hidden !important;
  padding: 8px !important;
}
.footer-actions {
  flex-shrink: 0;
  min-height: 56px;
}
.footer-total-badge {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.footer-total-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #90a4ae;
  font-weight: 600;
}
.footer-total-amount {
  font-size: 1.9rem;
  font-weight: 800;
  color: #0277bd;
  letter-spacing: -0.5px;
  line-height: 1.1;
}
.footer-breakdown {
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  gap: 4px;
}
.footer-subtotal {
  font-size: 0.92rem;
  color: #b0bec5;
  text-decoration: line-through;
}
.footer-chips {
  display: flex;
  gap: 4px;
}
.footer-arrow {
  margin: 0 8px;
}
.footer-total-main {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.3;
}
</style>
